/**
 * Lo que cambia de una campaña de puestos de mercado a otra: qué se pide, qué dice la ruleta y qué
 * preguntas extra lleva la visita al comerciante.
 *
 * La pantalla nació para Softys · Toallitas Húmedas (14-set) con todo eso escrito en el código. El
 * 26-oct llegó Ladysoft en mercados —misma forma de trabajo, otros premios y otra regla de compra—
 * y la elección era copiar la pantalla o sacar lo que cambia a la base. Sale a la base
 * (`campaigns.visitas_puesto_config`, 0334): la próxima campaña de mercados se configura con un
 * UPDATE, igual que ya se enciende con `visitas_puesto`.
 *
 * Sin configuración, la campaña se comporta EXACTAMENTE como Toallitas. Es lo que deja intacto lo
 * ya registrado y lo que ya está en la bandeja.
 */

export interface ConfigVisitasPuesto {
  /** El producto del pedido del comerciante: «¿Hizo pedido de …?». */
  productoPedido: string;
  /** La regla de la ruleta para quien compra, tal como se le dice en el puesto. */
  textoVenta: string;
  /** Los premios de la ruleta. Lista cerrada: es lo que se cuenta al cerrar. */
  premios: readonly string[];
  /** Pregunta si se le entregó la muestra al comerciante. */
  preguntaMuestra: boolean;
  /** Pregunta cuántos packs de regalo se entregaron por el pedido. */
  preguntaBonificacion: boolean;
  /**
   * Las dinámicas del mayorista, tal como las dice la lámina del cliente («Lima: 2 PL Clásica Larga, llévate 18
   * packs»). Si hay alguna, al pedido se le pregunta con cuál compró; la pantalla agrega sola «Sin dinámica».
   */
  dinamicasMayorista: readonly string[];
  /**
   * El nombre de cada formulario, en el botón de «Mi turno» y arriba de la pantalla. `null` = los de siempre
   * («visita a comerciante», «visita de venta»). Existe por el daily del 28-oct: con los nombres genéricos, una venta
   * de 42 packs a un cliente final terminó cargada como visita al mayorista.
   */
  rotuloComerciante: string | null;
  rotuloVenta: string | null;
}

/** La opción que la pantalla agrega a las dinámicas de la campaña. Se guarda tal cual. */
export const SIN_DINAMICA = 'Sin dinámica';

export const CONFIG_TOALLITAS: ConfigVisitasPuesto = {
  productoPedido: 'toallitas húmedas',
  textoVenta: 'Por la compra de 3 unidades de toallitas Babysec gira la ruleta.',
  premios: ['Papel toalla', 'Ladysoft', 'Elite Celeste', 'TH multiusos'],
  preguntaMuestra: false,
  preguntaBonificacion: false,
  dinamicasMayorista: [],
  rotuloComerciante: null,
  rotuloVenta: null,
};

/**
 * Lee la configuración tal como viene de la base.
 *
 * Una clave que falta toma el valor de Toallitas. Una clave que está pero viene mal **no** se
 * reemplaza en silencio: lanza. La alternativa sería mostrarle a una impulsadora de Ladysoft los
 * premios de Toallitas, y eso no da error en ninguna parte: llega a la bandeja como dato.
 */
export function leerConfigVisitasPuesto(crudo: unknown): ConfigVisitasPuesto {
  if (crudo === null || crudo === undefined) return CONFIG_TOALLITAS;
  if (typeof crudo !== 'object' || Array.isArray(crudo)) {
    throw new Error('La configuración de puestos de esta campaña está mal escrita.');
  }
  const c = crudo as Record<string, unknown>;

  const texto = (clave: string, defecto: string): string => {
    const v = c[clave];
    if (v === undefined) return defecto;
    if (typeof v !== 'string' || v.trim() === '') throw malo(clave);
    return v.trim();
  };
  const siNo = (clave: string, defecto: boolean): boolean => {
    const v = c[clave];
    if (v === undefined) return defecto;
    if (typeof v !== 'boolean') throw malo(clave);
    return v;
  };

  const lista = (clave: string, defecto: readonly string[], vaciaVale: boolean): readonly string[] => {
    const v = c[clave];
    if (v === undefined) return defecto;
    if (!Array.isArray(v) || (!vaciaVale && v.length === 0)
      || v.some((x) => typeof x !== 'string' || x.trim() === '')) {
      throw malo(clave);
    }
    return v.map((x) => (x as string).trim());
  };

  return {
    productoPedido: texto('producto_pedido', CONFIG_TOALLITAS.productoPedido),
    textoVenta: texto('texto_venta', CONFIG_TOALLITAS.textoVenta),
    premios: lista('premios', CONFIG_TOALLITAS.premios, false),
    preguntaMuestra: siNo('pregunta_muestra', CONFIG_TOALLITAS.preguntaMuestra),
    preguntaBonificacion: siNo('pregunta_bonificacion', CONFIG_TOALLITAS.preguntaBonificacion),
    dinamicasMayorista: lista('dinamicas_mayorista', CONFIG_TOALLITAS.dinamicasMayorista, true),
    rotuloComerciante: c.rotulo_comerciante === undefined ? null : texto('rotulo_comerciante', ''),
    rotuloVenta: c.rotulo_venta === undefined ? null : texto('rotulo_venta', ''),
  };
}

/** Los nombres que muestran «Mi turno» y la pantalla de puestos. */
export interface RotulosVisitasPuesto {
  comerciante: string;
  venta: string;
  /** Debajo del botón de venta: la regla de la ruleta, solo si la campaña puso sus propios nombres. */
  ayudaVenta: string | null;
}

export const ROTULOS_DE_SIEMPRE: RotulosVisitasPuesto = {
  comerciante: 'Registrar visita a comerciante',
  venta: 'Registrar visita de venta',
  ayudaVenta: null,
};

/**
 * Los nombres de los botones para los turnos del día. Si las campañas con puestos de hoy dicen cosas distintas, van
 * los de siempre: elegir los de una sola sería decidir por el primero de la lista.
 */
export function rotulosDeLasCampanias(configs: readonly ConfigVisitasPuesto[]): RotulosVisitasPuesto {
  const propios = configs.map((c) => (c.rotuloComerciante !== null && c.rotuloVenta !== null
    ? { comerciante: c.rotuloComerciante, venta: c.rotuloVenta, ayudaVenta: c.textoVenta }
    : ROTULOS_DE_SIEMPRE));
  if (propios.length === 0) return ROTULOS_DE_SIEMPRE;
  const clave = (r: RotulosVisitasPuesto) => `${r.comerciante}|${r.venta}|${r.ayudaVenta ?? ''}`;
  return propios.every((r) => clave(r) === clave(propios[0])) ? propios[0] : ROTULOS_DE_SIEMPRE;
}

function malo(clave: string): Error {
  return new Error(
    `La configuración de puestos de esta campaña está mal escrita («${clave}»). Avisa a tu coordinador.`,
  );
}
